import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "@/services/api.js";
import { Trophy } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const getRankStyle = (index) => {
  if (index === 0) return "text-yellow-400 font-bold";
  if (index === 1) return "text-zinc-300 font-bold";
  if (index === 2) return "text-amber-600 font-bold";
  return "text-zinc-500";
};

const LeaderboardPage = () => {
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetchLeaderboard = async () => {
      try {
        const response = await api.get("/match/leaderboard/all");
        const data = Array.isArray(response.data) ? response.data : [];
        const mapped = data.map((p) => ({
          id: p.userId,
          username: p.username,
          wins: p.wins,
          gamesPlayed: p.totalMatches,
          losses: p.totalMatches - p.wins,
        }));
        setPlayers(mapped);
      } catch (err) {
        toast.error(t("leaderboard_fetchError"));
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);


  const sorted = [...players].sort(
    (a, b) => b.wins / b.gamesPlayed - a.wins / a.gamesPlayed
  );

  const totalGames = sorted.reduce((sum, p) => sum + p.gamesPlayed, 0);
  // const topWinRate = ((sorted[0].wins / sorted[0].gamesPlayed) * 100).toFixed(1);
  // const avgGames = Math.round(totalGames / sorted.length);

  const topWinRate = sorted.length > 0
    ? ((sorted[0].wins / sorted[0].gamesPlayed) * 100).toFixed(1)
    : 0;

  const avgGames = sorted.length > 0
    ? Math.round(totalGames / sorted.length)
    : 0;

  if (loading) return <div className="text-zinc-400 text-center py-20">{t('leaderboard_loading')}</div>;

  return (
    <div className="min-h-[calc(100vh-64px)] px-4 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={16} className="text-violet-400" />
            <span className="text-xs font-mono uppercase tracking-widest text-violet-400">
              {t('leaderboard_rankings')}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-white">{t('leaderboard_title')}</h1>
          <p className="mt-1 text-zinc-500 text-sm">
            {t('leaderboard_subtitle')}
          </p>
        </div>

        {/* Stats summary */}
        <div className="rounded-xl border border-white/8 bg-white/5 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-zinc-400 text-xs uppercase">{t('leaderboard_totalPlayers')}</div>
              <div className="text-white text-lg font-bold">{sorted.length}</div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs uppercase">{t('leaderboard_totalGames')}</div>
              <div className="text-violet-400 text-lg font-bold">{totalGames}</div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs uppercase">{t('leaderboard_topWinRate')}</div>
              <div className="text-green-400 text-lg font-bold">{topWinRate}%</div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs uppercase">{t('leaderboard_avgGames')}</div>
              <div className="text-blue-400 text-lg font-bold">{avgGames}</div>
            </div>
          </div>
        </div>

        {/* Leaderboard table */}
        <div className="rounded-xl border border-white/8 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/8 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium w-16">
                  {t('leaderboard_rank')}
                </TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium">
                  {t('leaderboard_player')}
                </TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                  {t('leaderboard_wins')}
                </TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                  {t('leaderboard_losses')}
                </TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                  {t('leaderboard_winRate')}
                </TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">
                  {t('leaderboard_games')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((player, index) => {
                const winRate = player.gamesPlayed > 0 ? ((player.wins / player.gamesPlayed) * 100).toFixed(1) : 0;
                return (
                  <TableRow
                    key={player.id}
                    className={`border-white/5 hover:bg-white/3 transition-colors ${index < 3 ? "bg-white/[0.02]" : ""}`}
                  >
                    <TableCell className={getRankStyle(index)}>
                      #{index + 1}
                    </TableCell>
                    <TableCell className="text-white text-sm font-medium">
                      {player.username}
                    </TableCell>
                    <TableCell className="text-green-400 text-sm text-center">
                      {player.wins}
                    </TableCell>
                    <TableCell className="text-red-400 text-sm text-center">
                      {player.losses}
                    </TableCell>
                    <TableCell className="text-blue-400 text-sm text-center">
                      {winRate}%
                    </TableCell>
                    <TableCell className="text-zinc-300 text-sm text-center">
                      {player.gamesPlayed}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
